import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type RecorderStatus = "idle" | "recording" | "stopped";

const createBlankLevels = (bars: number) =>
  Array.from({ length: bars }, () => 0);

const getAudioContextConstructor = () => {
  if (typeof window === "undefined") {
    return undefined;
  }

  return (
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext
  );
};

export interface AudioRecorderOptions {
  bars?: number;
}

export const useAudioRecorder = (options?: AudioRecorderOptions) => {
  const bars = options?.bars ?? 48;
  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [levels, setLevels] = useState<number[]>(() => createBlankLevels(bars));

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const animationFrameRef = useRef<number>(0);
  const analyserNodeRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  const isSupported = useMemo(() => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return false;
    }

    return !!(
      typeof navigator.mediaDevices?.getUserMedia === "function" && getAudioContextConstructor()
    );
  }, []);

  const clearAnimation = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = 0;
    }
  }, []);

  const teardownAudio = useCallback(() => {
    clearAnimation();
    analyserNodeRef.current?.disconnect();
    analyserNodeRef.current = null;

    audioContextRef.current?.close().catch(() => undefined);
    audioContextRef.current = null;
  }, [clearAnimation]);

  const stopStreamTracks = useCallback(() => {
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
  }, []);

  const reset = useCallback(() => {
    if (mediaRecorderRef.current) {
      // Detach onstop handler to prevent blob creation on cancel
      mediaRecorderRef.current.onstop = null;
    }
    clearAnimation();
    teardownAudio();
    stopStreamTracks();
    chunksRef.current = [];
    setAudioBlob(null);
    setLevels(createBlankLevels(bars));
    setStatus("idle");
    setError(null);
  }, [bars, stopStreamTracks, teardownAudio, clearAnimation]);

  const startRecording = useCallback(async () => {
    if (!isSupported) {
      setError("Audio recording is not supported in this browser.");
      return;
    }

    if (status === "recording") {
      return;
    }

    try {
      setError(null);
      setAudioBlob(null);
      setLevels(createBlankLevels(bars));

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      mediaStreamRef.current = stream;
      chunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        stopStreamTracks();
        teardownAudio();

        if (chunksRef.current.length) {
          const blob = new Blob(chunksRef.current, {
            type: chunksRef.current[0].type || "audio/webm",
          });
          setAudioBlob(blob);
        }

        setStatus("stopped");
        setLevels((prev) =>
          prev.map((level) => Math.max(level * 0.5, 0.05))
        );
      };

      const AudioContextCtor = getAudioContextConstructor();
      if (AudioContextCtor) {
        const audioContext = new AudioContextCtor();
        // Ensure context is running (Safari keeps new contexts suspended)
        if (audioContext.state === "suspended") {
          try {
            await audioContext.resume();
          } catch (resumeError) {
            console.warn("Unable to resume audio context", resumeError);
          }
        }
        audioContextRef.current = audioContext;
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.6;
        source.connect(analyser);
        analyserNodeRef.current = analyser;
        const animate = () => {
          const analyserNode = analyserNodeRef.current;
          if (!analyserNode) {
            return;
          }
          const bufferLength = analyserNode.fftSize;
          const timeDomainData = new Uint8Array(bufferLength);
          analyserNode.getByteTimeDomainData(timeDomainData);

          const sliceSize = Math.max(1, Math.floor(bufferLength / bars));
          const newLevels: number[] = [];

          for (let i = 0; i < bars; i++) {
            const start = i * sliceSize;
            let peak = 0;

            for (let j = 0; j < sliceSize && start + j < bufferLength; j++) {
              const byte = timeDomainData[start + j];
              const normalizedSample = Math.abs(byte - 128) / 128;
              if (normalizedSample > peak) {
                peak = normalizedSample;
              }
            }

            const amplified = Math.pow(Math.min(1, peak * 4), 0.75);
            const normalized = Math.min(1, Math.max(amplified, peak * 2));
            newLevels.push(normalized);
          }

          setLevels((previous) => {
            const current =
              previous.length === bars ? previous : createBlankLevels(bars);
            return current.map((prevLevel, index) => {
              const target = newLevels[index] ?? 0;
              const clampedTarget = Math.max(target, 0.05);
              return prevLevel * 0.3 + clampedTarget * 0.7;
            });
          });
          animationFrameRef.current = requestAnimationFrame(animate);
        };

        animate();
      }

      recorder.start();
      setStatus("recording");
    } catch (err) {
      console.error("Failed to start recording", err);
      reset();
      setError(
        err instanceof Error
          ? err.message
          : "Failed to start audio recording."
      );
    }
  }, [bars, isSupported, reset, status, stopStreamTracks, teardownAudio]);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) {
      return;
    }

    if (recorder.state === "inactive") {
      return;
    }

    try {
      recorder.stop();
      setStatus("stopped");
    } catch (err) {
      console.error("Failed to stop recording", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to stop audio recording."
      );
    }
  }, []);

  useEffect(() => {
    return () => {
      reset();
      teardownAudio();
    };
  }, [reset, teardownAudio]);

  return {
    status,
    isRecording: status === "recording",
    audioBlob,
    error,
    levels,
    isSupported,
    startRecording,
    stopRecording,
    reset,
  };
};
